import React, { useState } from 'react';
import { ActionButton } from '@src/components/ActionButton';
import { style } from 'typestyle';
import { LocalizedStrings } from '@src/utils/localizedStrings';
import Tooltip from '@mui/material/Tooltip';
import { WidgetScriptIcon } from '../../Icons/WidgetScriptIcon';
import { WidgetTokenIcon } from '../../Icons/WidgetTokenIcon';
import { WidgetEmbedIcon } from '../../Icons/WidgetEmbedIcon';
import { WidgetView } from 'common';
import { WIDGET_CDN_URL, ENV, API_URL, WIDGET_VERSION } from '@src/config';
import { getSourceType } from '@src/pages/Widget/utils/sourceType';
import { ADALONG_COLORS } from '@src/styles';
import type { EditedWidget } from '@src/pages/Widget/EditPage';

interface WidgetScriptProps {
  remoteWidget: WidgetView;
  editedWidget: EditedWidget;
}

const getCode = (apiKey: string, type: string) => {
  const majorVersion = String(WIDGET_VERSION).split('.')[0];
  return `
<!--- 
AdAlong Widget : Use the data-${type}s attribute on #adalong-widget to override the fallback.
The value must be a stringified array with the ${type} ids.

The 'hasAnalyticsConsent' parameter controls whether cookies are enabled in the Widget.
By default, it is set to true. Set it to false to disable cookies, or connect it to
your CMP value and use the 'setHasAnalyticsConsent' method to update consent at runtime.

Get more informations on https://www.npmjs.com/package/@adalong/widget/v/${majorVersion}
-->
<div id="adalong-widget"></div>
<script defer src="${WIDGET_CDN_URL}"></script>
<script>
  window.addEventListener('load', async () => {
      const { initializeAdalongWidget } = window.AdalongWidget;
      ${ENV === 'prod' 
        ? `const widget = await initializeAdalongWidget({
            token: '${apiKey}',
            config: {
              hasAnalyticsConsent: true, // set to false to disable cookies
            },
          });`
        : `const widget = await initializeAdalongWidget({
          token: '${apiKey}',
          config: {
              hasAnalyticsConsent: true, // set to false to disable cookies
              debug: {
                apiUrl: ${API_URL}, 
              },
            },
        });`}
      await widget.load('#adalong-widget');
  });
</script>
`.trim();
}

export const WidgetScript = ({ remoteWidget, editedWidget }: WidgetScriptProps) => {
  const [copiedType, setCopiedType] = useState<'script' | 'key' | null>(null);
  const apiKey = remoteWidget.token || '';
  const sourceType = getSourceType(remoteWidget);

  const hasUnsavedChanges = JSON.stringify(editedWidget.settings) !== JSON.stringify(remoteWidget.settings);
  const isDisabled = !apiKey || hasUnsavedChanges;

  const generateCode = () => {
    return getCode(apiKey, sourceType);
  };

  const handleCopyScript = () => {
    if (isDisabled) return;
    navigator.clipboard.writeText(generateCode()).then(() => {
      setCopiedType('script');
      setTimeout(() => setCopiedType(null), 2000);
    }).catch(() => {});
  };

  const handleCopyToken = () => {
    if (isDisabled) return;
    navigator.clipboard.writeText(apiKey).then(() => {
      setCopiedType('key');
      setTimeout(() => setCopiedType(null), 2000);
    }).catch(() => {});
  };

  const handleEmbedAsHTML = () => {
    const embedUrl = `/widget-embed-html?widgetId=${encodeURIComponent(remoteWidget._id)}`;
    window.open(embedUrl, '_blank', 'noopener,noreferrer');
  };

  const renderTooltipButton = (
    type: 'script' | 'key',
    onAction: () => void,
    children: React.ReactNode
  ) => {
    const tooltipMessage = type === 'script' ? lang.saveChangesToCopyScript : lang.saveChangesToCopyToken;
    
    return (
      <Tooltip 
        title={hasUnsavedChanges ? tooltipMessage : ""}
        disableHoverListener={!hasUnsavedChanges}
      >
        <span>
          <ActionButton
            variant="transparent"
            onAction={onAction}
            disabled={isDisabled}
          >
            {children}
          </ActionButton>
        </span>
      </Tooltip>
    );
  };

  return (
    <div className={widgetScriptClass}>
      <div className={buttonsContainerClass}>
        {renderTooltipButton('key', handleCopyToken, (
          <>
            {copiedType === 'key' ? lang.copied : lang.widgetToken}
            <span className={iconWrapperClass}>
              <WidgetTokenIcon color={ADALONG_COLORS.LIGHT_WHITE} />
            </span>
          </>
        ))}
        {renderTooltipButton('script', handleCopyScript, (
          <>
            {copiedType === 'script' ? lang.copied : lang.widgetScript}
            <span className={iconWrapperClass}>
              <WidgetScriptIcon color={ADALONG_COLORS.LIGHT_WHITE} />
            </span>
          </>
        ))}
        <ActionButton
          variant="transparent"
          onAction={handleEmbedAsHTML}
        >
          {lang.embedAsHTML}
          <span className={iconWrapperClass}>
            <WidgetEmbedIcon />
          </span>
        </ActionButton>
      </div>
    </div>
  );
};

const widgetScriptClass = style({
  display: 'flex',
  alignItems: 'center',
  gap: '15px',
});

const buttonsContainerClass = style({
  display: 'flex',
  flexDirection: 'row',
  gap: '15px',
  alignItems: 'center',
});

const iconWrapperClass = style({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  flexShrink: 0,
  marginLeft: '5px',
  verticalAlign: 'middle',
});

const lang = LocalizedStrings({
  en: {
    widgetScript: 'WIDGET SCRIPT',
    widgetToken: 'WIDGET TOKEN',
    embedAsHTML: 'EMBED AS HTML',
    copied: 'COPIED!',
    saveChangesToCopyScript: '⚠️ Please save or reset your changes to copy the script',
    saveChangesToCopyToken: '⚠️ Please save or reset your changes to copy the token',
  },
});
